import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  heroCheck,
  heroEyeSlash,
  heroPlus,
  heroWrenchScrewdriver,
} from '@ng-icons/heroicons/outline';
import { NgpSelectPortal } from 'ng-primitives/select';
import type { AgentToolDefinition } from '../../../core/api/generated/models/AgentToolDefinition';
import {
  UiButton,
  UiCheckbox,
  UiEmptyState,
  UiInput,
  UiLabel,
  UiPanelCard,
  UiRecordRow,
  UiSelect,
  UiSelectDropdown,
  UiSelectOption,
  UiSectionHeader,
  UiTextarea,
} from '../../../shared/ui';
import type { BuilderToolFieldView } from './agent-builder.types';
import { AgentBuilderStore } from './agent-builder.store';

type ToolOutputField = {
  readonly key: string;
  readonly label: string;
  readonly type: string;
  readonly description: string;
  readonly required: boolean;
};

@Component({
  selector: 'app-agent-tool-config-panel',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgIcon,
    NgpSelectPortal,
    UiButton,
    UiCheckbox,
    UiEmptyState,
    UiInput,
    UiLabel,
    UiPanelCard,
    UiRecordRow,
    UiSelect,
    UiSelectDropdown,
    UiSelectOption,
    UiSectionHeader,
    UiTextarea,
  ],
  providers: [provideIcons({ heroCheck, heroEyeSlash, heroPlus, heroWrenchScrewdriver })],
  template: `
    <section appUiPanelCard class="tool-config" [class.tool-config--compact]="compact()">
      <app-ui-section-header
        label="Campos e fluxo de dados"
        help="Defina input, preenchimento da IA, valores fixos e retorno para cada tool."
        density="compact"
        [divider]="true"
      >
        <div sectionMeta class="tool-config__stats" aria-label="Resumo das ferramentas">
          <span>{{ selectedToolCount() }} ativa(s)</span>
          <span>{{ configuredFieldCount() }} campo(s)</span>
        </div>
      </app-ui-section-header>

      @if (tools().length === 0) {
        <app-ui-empty-state
          compact
          icon="heroWrenchScrewdriver"
          title="Nenhuma tool para configurar"
          description="Selecione integrações e tools para configurar campos de entrada e saída."
        />
      } @else {
        <div class="tool-list">
          @for (tool of tools(); track tool.id) {
            <article appUiRecordRow [interactive]="false" class="tool-card" [class.tool-card--disabled]="!isSelected(tool.id)">
              <header class="tool-card__head">
                <div class="tool-card__title">
                  <button
                    appCheckbox
                    type="button"
                    [ngpCheckboxChecked]="isSelected(tool.id)"
                    (ngpCheckboxCheckedChange)="store.toggleTool(tool.id)"
                    [attr.aria-label]="
                      isSelected(tool.id)
                        ? 'Desativar ' + tool.displayName
                        : 'Ativar ' + tool.displayName
                    "
                  >
                    <ng-icon
                      name="heroCheck"
                      size="13"
                      aria-hidden="true"
                      class="opacity-0 transition-opacity group-data-[checked]:opacity-100"
                    />
                  </button>
                  <div>
                    <h3>{{ tool.displayName || tool.key }}</h3>
                    <p>{{ tool.description }}</p>
                  </div>
                </div>
                <span class="risk">{{ store.toolRiskLabel(tool.riskLevel) }}</span>
              </header>

              @if (isSelected(tool.id)) {
                <div class="tool-flow" aria-label="Fluxo da ferramenta">
                  <span>
                    <strong>Input</strong>
                    <small>{{ enabledFieldCount(tool) }} campo(s)</small>
                  </span>
                  <span aria-hidden="true">→</span>
                  <span>
                    <strong>{{ tool.displayName || tool.key }}</strong>
                    <small>{{ store.toolRiskLabel(tool.riskLevel) }}</small>
                  </span>
                  <span aria-hidden="true">→</span>
                  <span>
                    <strong>Output</strong>
                    <small>{{ outputFields(tool).length }} campo(s)</small>
                  </span>
                </div>

                <div class="field-list">
                  @for (field of store.toolFieldViews(tool); track field.key) {
                    @if (field.enabled) {
                      <div appUiRecordRow [interactive]="false" class="field-card">
                        <div class="field-card__head">
                          <div>
                            <strong>{{ field.label }}</strong>
                            <small
                              >{{ field.required ? 'Sempre necessário' : 'Adicional' }} ·
                              {{ fieldKindLabel(field) }}</small
                            >
                          </div>
                          <div class="field-actions">
                            <span class="field-mode">{{ fieldFillLabel(field) }}</span>
                            <div class="lock-toggle">
                              <button
                                appCheckbox
                                type="button"
                                [ngpCheckboxChecked]="field.locked"
                                (ngpCheckboxCheckedChange)="
                                  store.setToolFieldLocked(tool.id, field.key, $event)
                                "
                                [attr.aria-label]="'Fixar valor de ' + field.label"
                              >
                                <ng-icon
                                  name="heroCheck"
                                  size="13"
                                  aria-hidden="true"
                                  class="opacity-0 transition-opacity group-data-[checked]:opacity-100"
                                />
                              </button>
                              <span>Fixar</span>
                            </div>
                            @if (!field.required) {
                              <button
                                appButton
                                variant="ghost"
                                size="sm"
                                type="button"
                                (click)="store.hideOptionalToolField(tool.id, field.key)"
                              >
                                <ng-icon name="heroEyeSlash" size="14" aria-hidden="true" />
                                Ocultar
                              </button>
                            }
                          </div>
                        </div>
                        @if (field.description) {
                          <p class="field-description">{{ field.description }}</p>
                        }
                        @if (field.inheritedFromAgent) {
                          <p class="field-inherited">
                            Herdado do agente. Escolha um valor aqui para sobrescrever só nesta ferramenta.
                          </p>
                        }

                        <div class="field-flow" aria-label="Fluxo do campo">
                          <span>
                            <strong>Entrada</strong>
                            <small>{{ field.required ? 'obrigatória' : 'opcional' }}</small>
                          </span>
                          <span>
                            <strong>Preenchimento</strong>
                            <small>{{ fieldFillLabel(field) }}</small>
                          </span>
                          <span>
                            <strong>Saída</strong>
                            <small>{{ fieldPayloadLabel(field) }}</small>
                          </span>
                        </div>

                        @switch (field.kind) {
                          @case ('select') {
                            <button
                              appSelect
                              type="button"
                              [ngpSelectValue]="field.value"
                              (ngpSelectValueChange)="
                                store.setToolFieldDefault(tool.id, field.key, field.kind, $event)
                              "
                              [attr.aria-label]="'Valor padrão de ' + field.label"
                            >
                              <span>{{ selectedOptionLabel(field) || 'Sem valor padrão' }}</span>
                              <div *ngpSelectPortal appSelectDropdown>
                                @for (option of field.options; track option.value) {
                                  <div appSelectOption [ngpSelectOptionValue]="option.value">
                                    <span>{{ option.label }}</span>
                                  </div>
                                }
                              </div>
                            </button>
                          }
                          @case ('multi_select') {
                            <div class="option-grid">
                              @for (option of field.options; track option.value) {
                                <div class="option-check">
                                  <button
                                    appCheckbox
                                    type="button"
                                    [ngpCheckboxChecked]="multiValueSelected(field, option.value)"
                                    (ngpCheckboxCheckedChange)="
                                      store.toggleToolFieldMultiValue(
                                        tool.id,
                                        field.key,
                                        option.value,
                                        $event
                                      )
                                    "
                                    [attr.aria-label]="'Alternar ' + option.label"
                                  >
                                    <ng-icon
                                      name="heroCheck"
                                      size="13"
                                      aria-hidden="true"
                                      class="opacity-0 transition-opacity group-data-[checked]:opacity-100"
                                    />
                                  </button>
                                  <span>{{ option.label }}</span>
                                </div>
                              }
                            </div>
                          }
                          @case ('number') {
                            <input
                              appInput
                              type="number"
                              [value]="inputValue(field)"
                              (input)="
                                store.setToolFieldDefaultFromEvent(
                                  tool.id,
                                  field.key,
                                  field.kind,
                                  $event
                                )
                              "
                              [attr.aria-label]="'Valor padrão de ' + field.label"
                            />
                          }
                          @case ('boolean') {
                            <div class="option-check">
                              <button
                                appCheckbox
                                type="button"
                                [ngpCheckboxChecked]="field.value === true"
                                (ngpCheckboxCheckedChange)="
                                  store.setToolFieldDefault(tool.id, field.key, field.kind, $event)
                                "
                                [attr.aria-label]="'Valor padrão de ' + field.label"
                              >
                                <ng-icon
                                  name="heroCheck"
                                  size="13"
                                  aria-hidden="true"
                                  class="opacity-0 transition-opacity group-data-[checked]:opacity-100"
                                />
                              </button>
                              <span>Usar verdadeiro como padrão</span>
                            </div>
                          }
                          @case ('textarea') {
                            <textarea
                              appTextarea
                              rows="3"
                              [value]="inputValue(field)"
                              (input)="
                                store.setToolFieldDefaultFromEvent(
                                  tool.id,
                                  field.key,
                                  field.kind,
                                  $event
                                )
                              "
                              [attr.aria-label]="'Valor padrão de ' + field.label"
                            ></textarea>
                          }
                          @case ('json') {
                            <textarea
                              appTextarea
                              rows="4"
                              [value]="inputValue(field)"
                              (input)="
                                store.setToolFieldDefaultFromEvent(
                                  tool.id,
                                  field.key,
                                  field.kind,
                                  $event
                                )
                              "
                              [attr.aria-label]="'Valor estruturado de ' + field.label"
                            ></textarea>
                          }
                          @default {
                            <input
                              appInput
                              type="text"
                              [value]="inputValue(field)"
                              (input)="
                                store.setToolFieldDefaultFromEvent(
                                  tool.id,
                                  field.key,
                                  field.kind,
                                  $event
                                )
                              "
                              [attr.aria-label]="'Valor padrão de ' + field.label"
                            />
                          }
                        }

                        <label>
                          <span appUiLabel class="mb-1.5 block uppercase">Como preencher</span>
                          <textarea
                            appTextarea
                            rows="2"
                            [value]="field.guidance"
                            (input)="store.setToolFieldGuidance(tool.id, field.key, $event)"
                            placeholder="Ex.: peça confirmação antes de usar este campo quando a conversa estiver ambígua."
                          ></textarea>
                        </label>
                      </div>
                    }
                  }
                </div>

                @if (store.availableOptionalToolFields(tool).length > 0) {
                  <button
                    appSelect
                    class="optional-select"
                    type="button"
                    [ngpSelectValue]="''"
                    (ngpSelectValueChange)="store.addOptionalToolField(tool.id, $event)"
                    aria-label="Adicionar campo"
                  >
                    <span>Adicionar campo</span>
                    <ng-icon name="heroPlus" size="14" aria-hidden="true" />
                    <div *ngpSelectPortal appSelectDropdown>
                      @for (field of store.availableOptionalToolFields(tool); track field.key) {
                        <div appSelectOption [ngpSelectOptionValue]="field.key">
                          {{ field.label }}
                        </div>
                      }
                    </div>
                  </button>
                }

                <section class="output-contract" aria-label="Contrato de saída da ferramenta">
                  <div class="output-contract__head">
                    <div>
                      <strong>Saída da tool</strong>
                      <p>Contrato somente para inspeção. O retorno volta ao contexto do agente.</p>
                    </div>
                    <span>{{ outputFields(tool).length }} campo(s)</span>
                  </div>
                  @if (outputFields(tool).length === 0) {
                    <p class="output-contract__empty">A tool retorna um payload livre ou não publicou propriedades de saída.</p>
                  } @else {
                    <div class="output-list">
                      @for (field of outputFields(tool); track field.key) {
                        <div class="output-row">
                          <div>
                            <strong>{{ field.label }}</strong>
                            <small>{{ field.key }} · {{ field.type }}{{ field.required ? ' · obrigatório' : '' }}</small>
                          </div>
                          @if (field.description) {
                            <p>{{ field.description }}</p>
                          }
                        </div>
                      }
                    </div>
                  }
                </section>
              }
            </article>
          }
        </div>
      }
    </section>
  `,
  styles: [
    `
      :host {
        display: block;
        min-width: 0;
      }

      .tool-config {
        position: sticky;
        top: 0.75rem;
        display: grid;
        min-width: 0;
        max-height: calc(100dvh - 11rem);
        gap: 0.8rem;
        overflow: auto;
      }

      .tool-card__title h3,
      .tool-card__title p,
      .field-description {
        margin: 0;
      }

      .tool-config--compact {
        position: static;
        max-height: none;
      }

      .tool-card__title p,
      .field-description {
        margin-top: 0.2rem;
        color: var(--color-muted);
        font-size: 0.82rem;
        line-height: 1.45;
      }

      .field-inherited {
        margin: 0.35rem 0 0;
        color: var(--color-accent-strong);
        font-size: 0.78rem;
        font-weight: 640;
        line-height: 1.4;
      }

      .tool-config__stats {
        display: inline-flex;
        align-items: center;
        gap: 0.35rem;
        justify-content: flex-end;
        flex-wrap: wrap;
        white-space: nowrap;
      }

      .tool-config__stats span,
      .field-mode {
        border-radius: var(--radius-pill);
        background: var(--color-soft);
        color: var(--color-muted);
        padding: 0.22rem 0.52rem;
        font-size: 0.68rem;
        font-weight: 780;
      }

      .tool-list,
      .field-list {
        display: grid;
        gap: 0.65rem;
      }

      .tool-card {
        display: grid;
        grid-template-columns: minmax(0, 1fr);
        min-width: 0;
        gap: 0.75rem;
      }

      .tool-card--disabled {
        background: color-mix(in oklch, var(--color-panel) 76%, var(--color-soft));
        opacity: 0.78;
      }

      .tool-card__head,
      .tool-card__title,
      .field-card__head,
      .option-check {
        display: flex;
        align-items: flex-start;
        gap: 0.55rem;
      }

      .tool-card__head,
      .field-card__head {
        justify-content: space-between;
      }

      .field-actions,
      .lock-toggle {
        display: inline-flex;
        align-items: center;
        gap: 0.45rem;
        flex-wrap: wrap;
        justify-content: flex-end;
      }

      .lock-toggle {
        color: var(--color-muted);
        font-size: 0.73rem;
        font-weight: 720;
        white-space: nowrap;
      }

      .tool-card__title,
      .tool-card__title > div,
      .field-card__head > div {
        min-width: 0;
      }

      .tool-card__title h3,
      .field-card strong {
        color: var(--color-ink);
        font-size: 0.9rem;
        font-weight: 760;
      }

      .risk,
      .field-card small {
        color: var(--color-muted);
        font-size: 0.72rem;
        font-weight: 760;
      }

      .risk {
        border-radius: var(--radius-pill);
        background: var(--color-soft);
        padding: 0.2rem 0.5rem;
      }

      /* Field title and its meta line must stack, not run together inline
         (Angular strips inter-element whitespace → "NomeSempre necessário"). */
      .field-card__head > div:first-child strong,
      .field-card__head > div:first-child small {
        display: block;
      }

      .tool-flow,
      .field-flow {
        display: grid;
        align-items: center;
        border: 1px solid color-mix(in oklch, var(--color-line) 82%, transparent);
        border-radius: var(--radius-sm);
        background: color-mix(in oklch, var(--color-soft) 48%, transparent);
      }

      .tool-flow {
        grid-template-columns: minmax(0, 1fr) auto minmax(0, 1.15fr) auto minmax(0, 1fr);
        gap: 0.45rem;
        padding: 0.55rem;
      }

      .field-flow {
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 0.45rem;
        padding: 0.5rem;
      }

      .tool-flow > span:not([aria-hidden='true']),
      .field-flow > span {
        display: grid;
        min-width: 0;
        gap: 0.08rem;
      }

      .tool-flow > span[aria-hidden='true'] {
        color: color-mix(in oklch, var(--color-muted) 58%, transparent);
        font-size: 0.78rem;
        font-weight: 820;
      }

      .tool-flow strong,
      .field-flow strong {
        overflow: hidden;
        color: var(--color-ink);
        font-size: 0.75rem;
        font-weight: 820;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .tool-flow small,
      .field-flow small {
        overflow: hidden;
        color: var(--color-muted);
        font-size: 0.68rem;
        font-weight: 650;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .field-card {
        display: grid;
        grid-template-columns: minmax(0, 1fr);
        gap: 0.65rem;
      }

      .option-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(12rem, 1fr));
        gap: 0.45rem;
      }

      .option-check {
        align-items: center;
        color: var(--color-ink);
        font-size: 0.82rem;
        font-weight: 650;
      }

      .optional-select {
        width: min(18rem, 100%);
      }

      .output-contract {
        display: grid;
        gap: 0.65rem;
        border-top: 1px solid var(--color-line);
        padding-top: 0.75rem;
      }

      .output-contract__head {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 0.75rem;
      }

      .output-contract__head > div {
        min-width: 0;
      }

      .output-contract__head strong,
      .output-row strong {
        color: var(--color-ink);
        font-size: 0.84rem;
        font-weight: 760;
      }

      .output-contract__head p,
      .output-contract__empty,
      .output-row p {
        margin: 0.16rem 0 0;
        color: var(--color-muted);
        font-size: 0.74rem;
        line-height: 1.42;
      }

      .output-contract__head > span {
        flex: 0 0 auto;
        border-radius: var(--radius-pill);
        background: var(--color-soft);
        color: var(--color-muted);
        padding: 0.22rem 0.52rem;
        font-size: 0.68rem;
        font-weight: 760;
      }

      .output-list {
        display: grid;
        border: 1px solid var(--color-line);
        border-radius: var(--radius-sm);
        overflow: hidden;
      }

      .output-row {
        display: grid;
        grid-template-columns: minmax(10rem, 0.65fr) minmax(0, 1.35fr);
        gap: 0.75rem;
        padding: 0.58rem 0.68rem;
      }

      .output-row + .output-row {
        border-top: 1px solid var(--color-line);
      }

      .output-row > div,
      .output-row small {
        min-width: 0;
      }

      .output-row small {
        display: block;
        margin-top: 0.1rem;
        overflow-wrap: anywhere;
        color: var(--color-muted);
        font-size: 0.69rem;
      }

      @media (max-width: 760px) {
        .tool-config {
          position: static;
          max-height: none;
        }

        .tool-config__head,
        .tool-flow,
        .field-flow {
          grid-template-columns: 1fr;
        }

        .tool-flow > span[aria-hidden='true'] {
          display: none;
        }

        .tool-config__stats {
          justify-content: flex-start;
        }

        .field-card__head {
          flex-direction: column;
        }

        .field-actions {
          justify-content: flex-start;
          width: 100%;
        }
      }
    `,
  ],
})
export class AgentToolConfigPanelComponent {
  readonly store = inject(AgentBuilderStore);
  readonly toolIds = input<readonly string[] | null>(null);
  readonly compact = input(false);

  readonly tools = computed(() => {
    const requestedIds = this.toolIds();
    if (requestedIds) {
      const requested = new Set(requestedIds);
      return this.store.tools().filter((tool) => requested.has(tool.id));
    }
    const byId = new Map<string, AgentToolDefinition>();
    for (const tool of this.store.inferredTools()) byId.set(tool.id, tool);
    for (const tool of this.store.selectedTools()) byId.set(tool.id, tool);
    return [...byId.values()];
  });
  readonly selectedToolCount = computed(
    () => this.tools().filter((tool) => this.isSelected(tool.id)).length,
  );
  readonly configuredFieldCount = computed(() =>
    this.tools()
      .filter((tool) => this.isSelected(tool.id))
      .reduce(
        (count, tool) =>
          count + this.store.toolFieldViews(tool).filter((field) => field.enabled).length,
        0,
      ),
  );

  isSelected(toolId: string): boolean {
    return this.store.isSelected(this.store.selectedToolIds(), toolId);
  }

  fieldKindLabel(field: BuilderToolFieldView): string {
    const labels: Record<BuilderToolFieldView['kind'], string> = {
      text: 'Texto',
      textarea: 'Texto longo',
      number: 'Número',
      boolean: 'Sim/não',
      select: 'Seleção',
      multi_select: 'Lista',
      json: 'Estruturado',
    };
    return labels[field.kind];
  }

  selectedOptionLabel(field: BuilderToolFieldView): string {
    return field.options.find((option) => option.value === field.value)?.label ?? '';
  }

  multiValueSelected(field: BuilderToolFieldView, value: string): boolean {
    return Array.isArray(field.value) && field.value.includes(value);
  }

  inputValue(field: BuilderToolFieldView): string | number {
    if (typeof field.value === 'number' || typeof field.value === 'string') return field.value;
    if (Array.isArray(field.value)) return field.value.join(', ');
    if (typeof field.value === 'boolean') return field.value ? 'true' : 'false';
    return '';
  }

  enabledFieldCount(tool: AgentToolDefinition): number {
    return this.store.toolFieldViews(tool).filter((field) => field.enabled).length;
  }

  outputFields(tool: AgentToolDefinition): ToolOutputField[] {
    const schema = asRecord(tool.outputSchema);
    const properties = asRecord(schema['properties']);
    const required = new Set(
      Array.isArray(schema['required'])
        ? schema['required'].filter((value): value is string => typeof value === 'string')
        : [],
    );
    return Object.entries(properties).map(([key, value]) => {
      const property = asRecord(value);
      return {
        key,
        label: typeof property['title'] === 'string' ? property['title'] : humanizeSchemaKey(key),
        type: schemaTypeLabel(property),
        description: typeof property['description'] === 'string' ? property['description'] : '',
        required: required.has(key),
      };
    });
  }

  fieldFillLabel(field: BuilderToolFieldView): string {
    if (field.locked && this.hasFieldValue(field)) return 'pré-definido';
    if (this.hasFieldValue(field)) return 'padrão definido';
    if (field.guidance.trim()) return 'regra da IA';
    return field.required ? 'IA preenche' : 'sob demanda';
  }

  fieldPayloadLabel(field: BuilderToolFieldView): string {
    if (field.locked && this.hasFieldValue(field)) return 'fixo no payload';
    if (field.kind === 'json') return 'objeto estruturado';
    if (field.kind === 'multi_select') return 'lista no payload';
    return 'payload da tool';
  }

  private hasFieldValue(field: BuilderToolFieldView): boolean {
    if (field.value === null) return false;
    if (typeof field.value === 'string') return field.value.trim().length > 0;
    if (Array.isArray(field.value)) return field.value.length > 0;
    return true;
  }
}

function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

function humanizeSchemaKey(key: string): string {
  return key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toLocaleUpperCase('pt-BR'));
}

function schemaTypeLabel(property: Record<string, unknown>): string {
  const rawType = property['type'];
  const type = Array.isArray(rawType)
    ? rawType.find((value): value is string => typeof value === 'string' && value !== 'null')
    : rawType;
  const labels: Record<string, string> = {
    array: 'lista',
    boolean: 'sim/não',
    integer: 'inteiro',
    number: 'número',
    object: 'objeto',
    string: 'texto',
  };
  return typeof type === 'string' ? labels[type] ?? type : 'valor';
}
